<script setup>
import {
  computed, inject, ref, watch, onMounted, onUnmounted,
} from 'vue';
import { Dialog, DialogPanel } from '@headlessui/vue';
import { Bars3Icon, XMarkIcon } from '@heroicons/vue/24/outline';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';
import Trans from '@/i18n/translation';
import gamebookLogoUrl from '@/assets/cyberalarm_logo_white.png';
import { useGlobalState } from '@/store/user.store';
import LogoutIcon from '@/components/CustomIcons/LogoutIcon.vue';
import { trackEvent } from '@/services/analytics/googleAnalytics.js';
import LandingCtaButton from '@/components/landing/v2/LandingCtaButton.vue';
import LanguageSwitcher from "@/components/LanguageSwitcher.vue";

const i18n = useI18n();
const homeNavigation = Trans.i18nRoute({ name: 'home' });
const loginNavigation = Trans.i18nRoute({ name: 'login' });
const dashboardNavigation = Trans.i18nRoute({ name: 'dashboard' });
const getStartedNavigation = Trans.i18nRoute({ name: 'register' });
const activeSection = ref(inject('activeSection'));

// New Navigation Data for V2
const navigationData = [
  { name: i18n.t('landingPage.v2.nav.humanRisk'), href: Trans.i18nRoute({ name: 'home', hash: '#threats' }) },
  { name: i18n.t('landingPage.v2.nav.impactSimulation'), href: Trans.i18nRoute({ name: 'home', hash: '#video' }) },
  { name: i18n.t('landingPage.v2.nav.compliance'), href: Trans.i18nRoute({ name: 'home', hash: '#comparison' }) },
  { name: i18n.t('landingPage.v2.nav.howItWorks'), href: Trans.i18nRoute({ name: 'home', hash: '#how-it-works' }) },
  { name: i18n.t('landingPage.v2.nav.useCases'), href: Trans.i18nRoute({ name: 'home', hash: '#faq' }) },
];

const extractHash = (href) => {
  if (typeof href === 'object' && href.hash) {
    return href.hash.replace('#', '');
  }
  const hrefString = typeof href === 'string' ? href : href?.toString?.() || '';
  const index = hrefString.indexOf('#');
  return index !== -1 ? hrefString.slice(index + 1) : '';
};

let sectionTimer = null;
const lastTrackedSection = ref(null);

const isActive = (href) => {
  const targetHash = extractHash(href);
  return activeSection.value === targetHash;
};

watch(
  activeSection,
  (newVal) => {
    if (sectionTimer) {
      clearTimeout(sectionTimer);
    }

    if (newVal && newVal !== lastTrackedSection.value) {
      sectionTimer = setTimeout(() => {
        const eventName = `viewed_${newVal}`;
        trackEvent(eventName, {
          event_category: 'LandingPage',
          event_label: newVal,
        });
        lastTrackedSection.value = newVal;
      }, 2000);
    }
  },
);

const navLinkClasses = computed(() => (href) => {
  const baseClasses = 'text-sm leading-6 transition-colors duration-300 ease-in-out font-medium';
  const isActiveLink = isActive(href);
  return `${baseClasses} ${isActiveLink ? 'text-terciary' : 'text-gray-100 hover:text-white'}`;
});
const { isAuthenticated } = useGlobalState();

const mobileMenuOpen = ref(false);

// Scroll Background Logic
const isScrolled = ref(false);
const handleScroll = () => {
  isScrolled.value = window.scrollY > 20;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

</script>

<template>
  <header class="fixed z-50 top-0 start-0 w-full transition-all duration-300 ease-in-out border-b border-transparent"
    :class="{
      'bg-transparent py-4': !isScrolled,
      'bg-slate-900 md:bg-slate-900/90 md:backdrop-blur-md shadow-md py-2 border-white/5': isScrolled
    }">
    <nav class="relative mx-auto flex  items-center justify-between px-6 lg:px-8">
      <div class="flex items-center gap-x-12">
        <RouterLink :to="homeNavigation" class="-m-1.5 p-1.5 inline-flex items-center gap-3">
          <!-- Adjust logo sizing/color if needed -->
          <img :src="gamebookLogoUrl" alt="Gamebook Logo" class="h-20 w-auto">
        </RouterLink>
      </div>

      <!-- Navigation Items -->
      <div class="hidden lg:flex lg:gap-x-10 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <RouterLink v-for="item in navigationData" :key="item.name" :to="item.href" class="relative group py-2">
          <span :class="navLinkClasses(item.href)">
            {{ item.name }}
          </span>
          <span
            class="absolute left-0 bottom-0 h-0.5 w-full bg-terciary origin-left transform scale-x-0 transition-transform duration-300 ease-out group-hover:scale-x-100"
            :class="{ 'scale-x-100': isActive(item.href) }" />
        </RouterLink>
      </div>

      <div class="flex lg:hidden">
        <button type="button"
          class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-200 hover:text-white"
          @click="mobileMenuOpen = true">
          <span class="sr-only">Open main menu</span>
          <Bars3Icon class="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
      <div class="hidden lg:flex items-center gap-x-6">
        <template v-if="isAuthenticated()">
          <RouterLink :to="dashboardNavigation"
            class="text-sm font-semibold leading-6 text-white hover:text-terciary transition-colors">
            {{ $t('dashboardBtn') }}
          </RouterLink>
          <RouterLink to="/logout"
            class="text-sm font-semibold leading-6 text-white hover:text-terciary transition-colors flex items-center">
            <LogoutIcon class="mr-2 h-4 w-4" />
            {{ $t('logOut') }}
          </RouterLink>
        </template>

        <template v-else>
          <LanguageSwitcher direction="down" />
          <RouterLink :to="loginNavigation"
            class="text-sm font-semibold leading-6 text-white hover:text-terciary transition-colors">
            {{ $t('logIn') }}
          </RouterLink>

          <LandingCtaButton class="!px-6 !py-2 !text-sm" sectionName="Navigation" :text="$t('landingPage.v2.nav.cta')">
          </LandingCtaButton>
        </template>
      </div>
    </nav>
    <Dialog class="lg:hidden" @close="mobileMenuOpen = false" :open="mobileMenuOpen">
      <div class="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm" />
      <DialogPanel
        class="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-slate-900 px-6 py-2 sm:max-w-sm ring-1 ring-white/10">
        <div class="flex items-center justify-between">
          <RouterLink @click="mobileMenuOpen = false" :to="homeNavigation" class="-m-1.5 p-1.5 flex items-center gap-2">
            <img :src="gamebookLogoUrl" alt="Gamebook Logo" class="h-20 w-auto">
          </RouterLink>
          <button type="button" class="-m-2.5 rounded-md p-2.5 text-gray-200 hover:text-white"
            @click="mobileMenuOpen = false">
            <span class="sr-only">{{ $t('nav.closeMenu') }}</span>
            <XMarkIcon class="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <div class="mt-6 flow-root">
          <div class="-my-6 divide-y divide-white/10">
            <div class="space-y-2 py-6">
              <RouterLink @click="mobileMenuOpen = false" v-for="item in navigationData" :key="item.name"
                :to="item.href" class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7"
                :class="isActive(item.href) ? 'text-terciary bg-white/10' : 'text-white hover:bg-white/5'">
                {{ item.name }}
              </RouterLink>
            </div>
            <div class="py-6 space-y-3">
              <template v-if="isAuthenticated()">
                <RouterLink @click="mobileMenuOpen = false" :to="dashboardNavigation"
                  class="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-white hover:bg-white/5">
                  {{ $t('dashboardBtn') }}
                </RouterLink>
                <RouterLink @click="mobileMenuOpen = false" to="/logout"
                  class="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-white hover:bg-white/5 flex items-center">
                  <LogoutIcon class="mr-2 h-5 w-5" />
                  {{ $t('logOut') }}
                </RouterLink>
              </template>
              <template v-else>
                <RouterLink @click="mobileMenuOpen = false" :to="loginNavigation"
                  class="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-white hover:bg-white/5">
                  {{ $t('logIn') }}
                </RouterLink>
                <div class="-mx-3 px-3 py-2">
                  <LanguageSwitcher direction="down" />
                </div>
                <div class="-mx-3 px-3">
                  <LandingCtaButton class="w-full text-center" sectionName="MobileNavigation"
                    :text="$t('landingPage.v2.nav.cta')">
                  </LandingCtaButton>
                </div>
              </template>
            </div>
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  </header>
</template>
